import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  useColorModeValue,
  Text,
  Button,
  HStack,
  Badge,
  Divider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";
import {
  FiPlus,
  FiMessageSquare,
  FiEye,
  FiX,
  FiArrowUp,
  FiTrash,
} from "react-icons/fi";
import { ticketAPI, userAPI } from "../../services";
import { useNavigate } from "react-router-dom";
import { TicketPriority, TicketStatus } from "types";
import { useAuthStore } from "stores/authStore";

interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdAt: string;
  updatedAt: string;
  messages: any[];
}

export const Support: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useAuthStore();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    priority: "LOW" as TicketPriority,
  });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadTickets();
  }, [page]);

  const loadTickets = async () => {
    try {
      const data = await ticketAPI.getTickets(page, limit);
      setTickets(data.tickets);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error("Error loading tickets:", error);
    }
  };

  const handleCloseTicket = async (id: string) => {
    try {
      await ticketAPI.closeTicket(id);
      loadTickets();
    } catch (error) {
      console.error("Error closing ticket:", error);
    }
  };

  const handleChangePriority = async (id: string, priority: TicketPriority) => {
    try {
      await ticketAPI.updateTicket(id, { priority });
      loadTickets();
    } catch (error) {
      console.error("Error changing priority:", error);
    }
  };

  const handleCreateTicket = async () => {
    try {
      await ticketAPI.createTicket(newTicket);
      onClose();
      loadTickets();
      setNewTicket({
        title: "",
        description: "",
        priority: "LOW" as TicketPriority,
      });
    } catch (error) {
      console.error("Error creating ticket:", error);
    }
  };

  const handleChangeStatus = async (id: string, status: TicketStatus) => {
    try {
      await ticketAPI.updateTicket(id, { status });
      loadTickets();
    } catch (error) {
      console.error("Error changing status:", error);
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    const colors = {
      OPEN: "blue",
      IN_PROGRESS: "orange",
      RESOLVED: "green",
      CLOSED: "gray",
    };
    return colors[status] || "gray";
  };

  const getPriorityColor = (priority: TicketPriority) => {
    const colors = {
      LOW: "green",
      MEDIUM: "blue",
      HIGH: "orange",
      URGENT: "red",
    };
    return colors[priority] || "gray";
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("support.title")}
              </Heading>
              <Text color="gray.500">{t("support.subtitle")}</Text>
            </VStack>

            {/* Tickets Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <HStack w="full" justify="space-between">
                <Heading size="md">{t("support.tickets.title")}</Heading>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="brand"
                  onClick={onOpen}
                >
                  {t("support.tickets.create")}
                </Button>
              </HStack>

              <Divider />

              {/* Tickets Table */}
              <Box overflowX="auto" w="100%">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t("support.tickets.table.title")}</Th>
                      <Th>{t("support.tickets.table.status")}</Th>
                      {user && user.isAdmin && (
                        <Th>{t("support.tickets.table.client")}</Th>
                      )}
                      <Th>{t("support.tickets.table.priority")}</Th>
                      <Th>{t("support.tickets.table.lastUpdate")}</Th>
                      <Th>{t("support.tickets.table.actions")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {tickets.map((ticket) => (
                      <Tr key={ticket.id}>
                        <Td>{ticket.title}</Td>
                        <Td>
                          {user && user.isAdmin ? (
                            <Tooltip
                              label={t("support.tickets.actions.changeStatus")}
                            >
                              <Select
                                value={ticket.status}
                                onChange={(e) =>
                                  handleChangeStatus(
                                    ticket.id,
                                    e.target.value as TicketStatus
                                  )
                                }
                              >
                                <option value="OPEN">OPEN</option>
                                <option value="IN_PROGRESS">IN_PROGRESS</option>
                                <option value="RESOLVED">RESOLVED</option>
                                <option value="CLOSED">CLOSED</option>
                              </Select>
                            </Tooltip>
                          ) : (
                            <Badge colorScheme="gray">
                              {t(`support.tickets.status.${ticket.status}`)}
                            </Badge>
                          )}
                        </Td>
                        {user && user.isAdmin && (
                          <Td>
                            {user.firstName} {user.lastName}{" "}
                            {user.cpf ? `- (${user.cpf})` : `- (${user.email})`}
                          </Td>
                        )}
                        <Td>
                          {user && user.isAdmin ? (
                            <Tooltip
                              label={t(
                                "support.tickets.actions.changePriority"
                              )}
                            >
                              <Select
                                value={ticket.priority}
                                onChange={(e) =>
                                  handleChangePriority(
                                    ticket.id,
                                    e.target.value as TicketPriority
                                  )
                                }
                              >
                                <option value="LOW">LOW</option>
                                <option value="MEDIUM">MEDIUM</option>
                                <option value="HIGH">HIGH</option>
                                <option value="URGENT">URGENT</option>
                              </Select>
                            </Tooltip>
                          ) : (
                            <Badge
                              colorScheme={getPriorityColor(ticket.priority)}
                            >
                              {t(`support.tickets.priority.${ticket.priority}`)}
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          {new Date(ticket.updatedAt).toLocaleDateString(
                            i18n.language === "pt-BR" ? "pt-BR" : "en-US"
                          )}
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label={t("support.tickets.actions.view")}>
                              <IconButton
                                aria-label="View ticket"
                                icon={<FiEye />}
                                size="sm"
                                onClick={() =>
                                  navigate(`/support/${ticket.id}`)
                                }
                              />
                            </Tooltip>
                            <Tooltip label={t("support.tickets.actions.reply")}>
                              <IconButton
                                aria-label="Reply to ticket"
                                icon={<FiMessageSquare />}
                                size="sm"
                                onClick={() =>
                                  navigate(`/support/${ticket.id}`)
                                }
                              />
                            </Tooltip>
                            {(ticket.status === "OPEN" ||
                              ticket.status === "IN_PROGRESS") && (
                              <Tooltip
                                label={t("support.tickets.actions.close")}
                              >
                                <IconButton
                                  aria-label="Close ticket"
                                  icon={<FiX />}
                                  size="sm"
                                  onClick={() => handleCloseTicket(ticket.id)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Add pagination controls */}
              <HStack spacing={2} justify="space-between" w="full">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                >
                  {t("common.previous")}
                </Button>
                <Text>
                  {t("common.page")} {page} / {totalPages}
                </Text>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page === totalPages}
                >
                  {t("common.next")}
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Create Ticket Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontFamily="heading">
            {t("support.tickets.modal.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t("support.tickets.modal.titleLabel")}</FormLabel>
                <Input
                  value={newTicket.title}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, title: e.target.value })
                  }
                  placeholder={t("support.tickets.modal.titlePlaceholder")}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  {t("support.tickets.modal.descriptionLabel")}
                </FormLabel>
                <Textarea
                  value={newTicket.description}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, description: e.target.value })
                  }
                  placeholder={t(
                    "support.tickets.modal.descriptionPlaceholder"
                  )}
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  {t("support.tickets.modal.priorityLabel")}
                </FormLabel>
                <Select
                  value={newTicket.priority}
                  onChange={(e) =>
                    setNewTicket({
                      ...newTicket,
                      priority: e.target.value as TicketPriority,
                    })
                  }
                >
                  <option value="LOW">
                    {t("support.tickets.modal.priority.low")}
                  </option>
                  <option value="MEDIUM" disabled={user?.plan === "FREE"}>
                    {t("support.tickets.modal.priority.medium")}
                  </option>
                  <option value="HIGH" disabled={user?.plan === "FREE"}>
                    {t("support.tickets.modal.priority.high")}
                  </option>
                  <option value="URGENT" disabled>
                    {t("support.tickets.modal.priority.urgent")}
                  </option>
                </Select>
              </FormControl>

              <Button
                colorScheme="brand"
                mr={3}
                onClick={handleCreateTicket}
                width="full"
              >
                {t("support.tickets.modal.submit")}
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Footer />
    </Box>
  );
};
